import { useTranslation } from "react-i18next";

import {
  getGetPaymentMethodsFromPpsPaymentMethodsUiGetQueryKey,
  useGetPaymentMethodsFromPpsPaymentMethodsUiGet,
} from "@/api-v3";
import { ENV } from "@/features/config";
import { entries } from "@/lib/object";

import {
  type AddressFieldType,
  type ClientType,
  type PaymentMethod,
  type Property,
  type AvailablePaymentMethodsResponse,
  type PropertyType,
  type FieldTypes,
  MethodProviderName,
} from "../types/available-payment-methods";

export const useAvailablePaymentMethods = () => {
  const { i18n } = useTranslation();
  const language = i18n.language.toLowerCase();

  const { data, isLoading, error } =
    useGetPaymentMethodsFromPpsPaymentMethodsUiGet<AvailablePaymentMethodsResponse>(
      {
        query: {
          queryKey: [
            ...getGetPaymentMethodsFromPpsPaymentMethodsUiGetQueryKey(),
            language,
          ],
          refetchOnWindowFocus: false,
          staleTime: Infinity,
          gcTime: Infinity,
          refetchOnMount: false,
          refetchOnReconnect: false,
          retry: false,
        },
      }
    );

  const duplicatedPaymentTypes = getDuplicatedPaymentTypes(data ?? []);
  const availablePaymentMethods = data ?? [];

  return {
    isLoadingAvailablePaymentMethods: isLoading,
    availablePaymentMethodsError: error,
    availablePaymentMethods: availablePaymentMethods.map(
      mapPaymentMethodToForm
    ),
    duplicatedPaymentTypes,
  };
};

/**
 * Finds payment types that appear more than once in the methods
 * @param methods Array of payment methods
 * @returns Object with payment types as keys and their count as values
 */
const getDuplicatedPaymentTypes = (
  methods: AvailablePaymentMethodsResponse
) => {
  const paymentTypeCounts = methods.reduce<Record<string, number>>(
    (acc, method) => {
      const type = method.payment_type;
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    },
    {}
  );

  // Filter to only include types that appear more than once
  return Object.entries(paymentTypeCounts)
    .filter(([, count]) => count > 1)
    .reduce<Record<string, number>>((acc, [type, count]) => {
      acc[type] = count;
      return acc;
    }, {});
};

type PropertyWithAllFieldTypes = Omit<Property, "property_name"> & {
  property_name: FieldTypes;
};

export type ExtendedProperty = PropertyWithAllFieldTypes & {
  options: { key: string; value: string }[];
  required: boolean;
};
export interface ExtendedForm {
  fields: ExtendedProperty[];
  name: string;
  title?: string;
  next?: ExtendedForm;
}

export type ExtendedPaymentMethod = PaymentMethod & {
  form: ExtendedForm;
};

const EVERVAULT_FIELD_NAMES = ["card_number", "expiration_date"] as const;
type EvervaultFieldName = (typeof EVERVAULT_FIELD_NAMES)[number];

const isEvervaultField = (
  methodProviderName: MethodProviderName,
  fieldName: string
): boolean =>
  methodProviderName === MethodProviderName.DEBIT_CARD_CHECKBOOK &&
  EVERVAULT_FIELD_NAMES.includes(fieldName as EvervaultFieldName);

const mapPaymentMethodToForm = (paymentMethod: PaymentMethod) => {
  const getActualType = (field: PropertyWithAllFieldTypes) => {
    if (field.enum && field.enum.length > 0 && !field.client_type) {
      return "radio";
    }

    if (field.client_type === null) {
      return "text";
    }
    return field.client_type;
  };

  const processFormFields = (
    properties: PropertyWithAllFieldTypes[],
    requiredFields: string[],
    name: string,
    title?: string
  ): ExtendedForm => {
    // Separate regular fields from object fields
    const regularFields = properties.filter(
      (property) => property.type !== "object"
    );
    const objectFields = properties.filter(
      (property) => property.type === "object"
    );

    // Process regular fields
    const fields = regularFields.map((field) => {
      // Evervault fields bypass schema validation — patterns and numeric
      // client_type would conflict with encrypted/MM-YY formatted values.
      const isEvervault = isEvervaultField(
        paymentMethod.method_provider_name,
        field.property_name
      );
      return {
        ...field,
        pattern: isEvervault ? null : field.pattern,
        options: field.enum ?? [],
        client_type: isEvervault ? null : (getActualType(field) as ClientType),
        required: requiredFields.includes(field.property_name),
      };
    });

    // Process object fields (nested forms)
    let nextForm: ExtendedForm | undefined = undefined;

    // We only process the first object field as the next form
    if (objectFields.length > 0) {
      const objectField = objectFields[0];

      if (objectField.properties) {
        // For object fields, we need to map the properties object
        // Create a new Property array for the nested form fields
        const nestedProperties: PropertyWithAllFieldTypes[] = [];

        // Process each property in the object
        if (
          objectField.property_name === "address" &&
          ENV.LOQATE.ENABLED &&
          ENV.LOQATE.KEY
        ) {
          nestedProperties.push({
            title: "Search Address",
            description: null,
            property_name: "address",
            pattern: null,
            type: "string",
            client_type: "search" as ClientType,
            maxLength: null,
            minLength: null,
            enum: null,
            properties: null,
            required_fields: null,
          });
        }
        entries(objectField.properties).forEach(([key, value]) => {
          // Create a new Property object with the correct structure
          const property = {
            title: value.title,
            description: null,
            property_name: key,
            pattern: null,
            type: (value.type || "string") as PropertyType,
            client_type: (value.client_type || null) as ClientType,
            maxLength: value.maxLength || null,
            minLength: value.minLength || null,
            enum: value.options ?? null,
            properties: null,
            required_fields: null,
          };
          nestedProperties.push(property);
        });

        // Create the nested form
        nextForm = {
          title: objectField.title,
          name: objectField.property_name,
          fields: nestedProperties.map((field) => ({
            ...field,
            options: field.enum ?? [],
            client_type: getActualType(field) as ClientType,
            required:
              objectField.required_fields?.includes(
                field.property_name as AddressFieldType
              ) ?? false,
          })),
          next: undefined,
        };

        // Check if any nested properties are objects (for recursive processing)
        const hasNestedObjects = nestedProperties.some(
          (property) => property.type === "object"
        );
        if (hasNestedObjects) {
          nextForm.next = processFormFields(
            nestedProperties,
            objectField.required_fields ?? [],
            objectField.title,
            objectField.property_name
          );
        }
      }
    }

    return {
      title,
      name,
      fields,
      next: nextForm,
    };
  };

  // Start with the initial form
  const form = processFormFields(
    paymentMethod.properties,
    paymentMethod.required_fields,
    "initial"
  );

  const extendedPaymentMethod: ExtendedPaymentMethod = {
    ...paymentMethod,
    form,
  };

  return extendedPaymentMethod;
};
